/**
 * Import d'un export **Password Dragon** XML (« File > Export > XML » de
 * Password Dragon : ses utilisateurs doivent pouvoir partir avec TOUT leur
 * coffre).
 *
 * Purement local, aucune E/S reseau : ce module transforme un export en
 * `ImportedEntry`. Le document `<PasswordManager>` porte une suite de
 * `<record>` : `Account-Name`, `User-Id`, `Password`, `URL`, `Notes`,
 * `Category` (`Unfiled` = sans categorie) et dix attributs libres
 * `Attribute-1` a `Attribute-10`, ecrits `null` quand ils sont vides (lu
 * comme le lit Bitwarden, `passworddragon-xml-importer.ts`).
 */
import type { EntryCustomField } from '@klavox/crypto-core';
import { csvFreeColumnField, joinNotes, type ImportedEntry } from './csv-import.js';
import { childNamed, childrenNamed, parseXml, type XmlNode } from './kdbx-import.js';
import { serverHost } from './typed-fields.js';

/** Elements d'un `<record>` repris par un role de l'entree ; tout autre element non vide devient un champ. */
const DRAGON_ROLES = new Set(['Account-Name', 'User-Id', 'Password', 'URL', 'Notes', 'Category']);

/** Categorie par defaut de Password Dragon : aucune categorie choisie, donc aucun dossier. */
const UNFILED = 'Unfiled';

/** Le noeud `<PasswordManager>` d'un export Password Dragon, ou `null` si le texte n'en est pas un. */
function dragonDocument(text: string): XmlNode | null {
  if (!/^[\s﻿]*(?:<\?xml[^>]*>\s*)?(?:<!--[\s\S]*?-->\s*)*<PasswordManager[\s/>]/u.test(text)) return null;
  try {
    return childNamed(parseXml(text), 'PasswordManager');
  } catch {
    return null;
  }
}

/** Texte d'un element, rogne ; vide, absent ou `null` (valeur vide de Password Dragon) : chaine vide. */
function valueOf(record: XmlNode, tag: string): string {
  const value = (childNamed(record, tag)?.text ?? '').trim();
  return value === 'null' ? '' : value;
}

/** Un export Password Dragon se reconnait a son document `<PasswordManager>`. */
export function isPasswordDragonXml(text: string): boolean {
  return dragonDocument(text) !== null;
}

/**
 * Une entree par `<record>`. Au-dela de Bitwarden (qui nomme `--` une fiche
 * sans nom et fait un identifiant de tout) : nom de repli = hote de
 * l'adresse puis identifiant ; une fiche sans identifiant, mot de passe ni
 * adresse est une note ; tout element inconnu non vide (pas seulement les
 * dix attributs) est garde en champ, masque sous un libelle de secret.
 * Mot de passe jamais rogne, `null` compris (c'est peut-etre le sien).
 */
function dragonEntry(record: XmlNode): ImportedEntry | null {
  const username = valueOf(record, 'User-Id');
  // Jamais rogne : une espace peut faire partie du mot de passe.
  const password = childNamed(record, 'Password')?.text ?? '';
  const url = valueOf(record, 'URL');
  const uris = url.length > 0 ? [url] : [];
  const notes = joinNotes([valueOf(record, 'Notes')]);
  const customFields: EntryCustomField[] = [];
  for (const child of record.children) {
    if (DRAGON_ROLES.has(child.tag) || child.text.trim() === 'null') continue;
    const field = csvFreeColumnField(child.tag, child.text);
    if (field !== null) customFields.push(field);
  }
  const category = valueOf(record, 'Category');
  const isLogin = username.length > 0 || password.length > 0 || uris.length > 0;
  const accountName = valueOf(record, 'Account-Name');
  if (accountName.length === 0 && !isLogin && notes.length === 0 && customFields.length === 0) return null;
  const host = uris.length > 0 ? serverHost(url).replace(/^www\./u, '') : '';
  return {
    type: isLogin ? 'login' : 'note',
    name: accountName || host || url || username || 'Sans nom',
    username,
    password,
    uris,
    notes,
    totpSecret: '',
    favorite: false,
    ...(customFields.length > 0 ? { customFields } : {}),
    ...(category.length > 0 && category !== UNFILED ? { folderPath: [category] } : {}),
  };
}

/** Parse un export Password Dragon XML : un `<record>` = une entree, rangee dans le dossier de sa categorie. */
export function parsePasswordDragonXml(text: string): ImportedEntry[] {
  const document = dragonDocument(text);
  if (document === null) throw new Error('export Password Dragon : structure inattendue');
  const entries: ImportedEntry[] = [];
  for (const record of childrenNamed(document, 'record')) {
    const entry = dragonEntry(record);
    if (entry !== null) entries.push(entry);
  }
  return entries;
}
